import React from 'react';
import { Alert, Empty, Spin, Tag, Timeline, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBrain,
  faCheck,
  faCommentSms,
  faEnvelope,
  faFileShield,
  faPaperPlane,
  faUserDoctor,
  faVial
} from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';

const SERVICE_ICONS = { mentalHealth: faBrain, insurance: faFileShield, doctor: faUserDoctor };
const ENROLLMENT_STATUS = {
  new: 'New',
  in_progress: 'In progress',
  enrolled: 'Enrolled',
  not_eligible: 'Not eligible',
  closed: 'Closed'
};
const NOT_SENT = ['failed', 'invalid', 'not_sent', 'bounced', 'dropped'];

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const TINT = '#E6FAF8';
const card = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  padding: 18,
  background: COLORS.$white
};
const muted = { fontSize: 12.5, color: COLORS.textLight, lineHeight: 1.6 };

export const Section = ({ icon, title, children, first = false }) => (
  <section style={{ marginTop: first ? 0 : 28 }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontWeight: 600,
      fontSize: 15,
      color: COLORS.textDark,
      paddingBottom: 10,
      marginBottom: 16,
      borderBottom: `1px solid ${COLORS.border}`
    }}
    >
      {icon && <FontAwesomeIcon icon={icon} style={{ color: COLORS.$deepTiffany }} />}
      {title}
    </div>
    {children}
  </section>
);

const StepDot = ({ state, index }) => {
  const base = {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 600,
    flexShrink: 0
  };
  if (state === 'done') {
    return <span style={{ ...base, background: COLORS.$deepTiffany, color: COLORS.$white }}><FontAwesomeIcon icon={faCheck} /></span>;
  }
  if (state === 'current') {
    return <span style={{ ...base, background: TINT, color: COLORS.$deepTiffany, border: `2px solid ${COLORS.$deepTiffany}` }}>{index + 1}</span>;
  }
  return <span style={{ ...base, color: COLORS.textMuted, border: `1.5px solid ${COLORS.border}` }}>{index + 1}</span>;
};

// Vertical so long step names never collide in narrow cards.
const StepList = ({ steps, stage }) => (
  <div>
    {steps.map((title, i) => {
      const state = i < stage ? 'done' : i === stage ? 'current' : 'todo';
      const last = i === steps.length - 1;
      return (
        <div key={title} style={{ display: 'flex', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <StepDot state={state} index={i} />
            {!last && (
              <span style={{ width: 2, flex: 1, minHeight: 12, background: i < stage ? COLORS.$deepTiffany : COLORS.border }} />
            )}
          </div>
          <div style={{
            paddingBottom: last ? 0 : 12,
            paddingTop: 2,
            fontSize: 13,
            fontWeight: state === 'current' ? 600 : 400,
            color: state === 'todo' ? COLORS.textMuted : COLORS.textDark
          }}
          >
            {title}
          </div>
        </div>
      );
    })}
  </div>
);

const ServiceCard = ({ service }) => {
  const opened = service.links.filter((l) => l.clickCount || l.viewCount);
  const clicks = service.links.reduce((n, l) => n + (l.clickCount || 0), 0);
  const notes = [
    service.messageCount > 0 && `${service.messageCount} message${service.messageCount === 1 ? '' : 's'} sent · last ${fmtDay(service.lastMessageAt)}`,
    service.links.length > 0 && (opened.length
      ? `Link opened${clicks ? ` ${clicks} time${clicks === 1 ? '' : 's'}` : ''} · last ${fmtDay(opened[0].lastClickedAt || opened[0].firstViewedAt)}`
      : 'Link not opened yet'),
    service.key === 'doctor' && service.requested && service.messageCount === 0 && 'No automated follow-up yet. Reach out directly.',
    service.stage < 0 && 'Nothing sent.'
  ].filter(Boolean);
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: TINT,
          color: COLORS.$deepTiffany,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
        >
          <FontAwesomeIcon icon={SERVICE_ICONS[service.key]} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14.5, lineHeight: 1.3, color: COLORS.textDark }}>{service.label}</div>
          <div style={{ marginTop: 6 }}>
            {service.requested
              ? <Tag color="cyan" style={{ marginInlineEnd: 0 }}>Requested at intake</Tag>
              : <Tag style={{ marginInlineEnd: 0 }}>{service.stage >= 0 ? 'Not marked at intake' : 'Not requested'}</Tag>}
          </div>
        </div>
      </div>

      <StepList steps={service.steps} stage={service.stage} />

      {(notes.length > 0 || service.enrollment) && (
        <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, ...muted }}>
          {notes.map((n) => <div key={n}>{n}</div>)}
          {service.enrollment && (
            <div style={{ marginTop: notes.length ? 8 : 0 }}>
              <Tag color="cyan">{ENROLLMENT_STATUS[service.enrollment.status] || service.enrollment.status}</Tag>
              {service.enrollment.referenceCode} · submitted {fmtDay(service.enrollment.submittedAt)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MessageItem = ({ m }) => {
  const failed = NOT_SENT.includes(m.status);
  return (
    <div style={{ paddingBottom: 6 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: COLORS.textDark }}>{m.label}</span>
        {failed && (
          <Tooltip title={m.error || ''}>
            <Tag color="warning" style={{ marginInlineEnd: 0 }}>{m.status === 'not_sent' ? 'Not sent' : 'Not delivered'}</Tag>
          </Tooltip>
        )}
        {m.openedAt && <Tag color="cyan" style={{ marginInlineEnd: 0 }}>Opened</Tag>}
        {m.clickedAt && <Tag color="cyan" style={{ marginInlineEnd: 0 }}>Clicked</Tag>}
      </div>
      <div style={{ ...muted, marginTop: 4 }}>
        {fmt(m.at)} · {m.channel === 'sms' ? 'Text' : 'Email'}
        {m.automationName && m.automationName !== m.label ? ` · ${m.automationName}` : ''}
        {!m.toMember && m.to ? ` · sent to ${m.to}` : ''}
      </div>
      {m.body && (
        <div style={{
          marginTop: 10,
          padding: '10px 14px',
          background: TINT,
          borderRadius: 12,
          fontSize: 13,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          maxWidth: 560
        }}
        >
          {m.body}
        </div>
      )}
    </div>
  );
};

/** What the member asked for, what we sent them, and what they did with it. */
const CarePlanTab = ({ data, loading, error }) => {
  if (loading && !data) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  if (error) return <Alert type="warning" showIcon message={error} />;
  if (!data) return null;

  const { consent, services, results, messages } = data;
  const resultSteps = [
    [results.activatedAt, 'Kit activated'],
    [results.sampleReceivedAt, 'Sample received by lab'],
    [results.resultsReadyAt, 'Results ready'],
    [results.emailSentAt, 'Results email sent'],
    [results.smsSentAt, 'Results text sent']
  ].filter(([at]) => at);
  const resultClicks = (results.links || []).reduce((n, l) => n + (l.clickCount || 0), 0);

  return (
    <div style={{ padding: '4px 2px 24px' }}>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'center',
        padding: '12px 16px',
        background: '#F7F9FA',
        borderRadius: 10
      }}
      >
        {consent ? (
          <>
            <Tag color={consent.transactionalSms ? 'cyan' : 'warning'} style={{ marginInlineEnd: 0 }}>
              {consent.transactionalSms ? 'OK to text' : 'No text consent'}
            </Tag>
            <span style={muted}>Consent from {fmtDay(consent.at)}</span>
          </>
        ) : (
          <Tag style={{ marginInlineEnd: 0 }}>No consent form on file</Tag>
        )}
        {data.answers?.answeredAt && <span style={muted}>· Intake answers from {fmtDay(data.answers.answeredAt)}</span>}
      </div>

      <Section title="Services">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {services.map((s) => <ServiceCard key={s.key} service={s} />)}
        </div>
      </Section>

      <Section icon={faVial} title="Lab results">
        {resultSteps.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {resultSteps.map(([at, label]) => (
              <div key={label} style={{ ...card, padding: '12px 14px' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.textDark }}>{label}</div>
                <div style={{ ...muted, marginTop: 2 }}>{fmtDay(at)}</div>
              </div>
            ))}
            {results.links?.length > 0 && (
              <div style={{ ...card, padding: '12px 14px' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.textDark }}>Results link</div>
                <div style={{ ...muted, marginTop: 2 }}>{resultClicks ? `Opened ${resultClicks} time${resultClicks === 1 ? '' : 's'}` : 'Not opened yet'}</div>
              </div>
            )}
          </div>
        ) : (
          <div style={muted}>No lab kit on file.</div>
        )}
      </Section>

      <Section icon={faPaperPlane} title="Messages sent">
        {messages.length ? (
          <Timeline
            style={{ marginTop: 8, paddingLeft: 4 }}
            items={messages.map((m) => ({
              key: `${m.channel}-${m._id}`,
              color: NOT_SENT.includes(m.status) ? 'orange' : COLORS.$mainTiffany,
              dot: <FontAwesomeIcon icon={m.channel === 'sms' ? faCommentSms : faEnvelope} style={{ color: COLORS.$deepTiffany, fontSize: 14 }} />,
              children: <MessageItem m={m} />
            }))}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No automated texts or emails yet" />
        )}
      </Section>
    </div>
  );
};

export default CarePlanTab;
